import type { FC } from 'react';
import { useIntl } from 'react-intl';

import { Redirect } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useFeatureSwitch } from '@xing-com/hub';

import { FollowedCompanies } from '../../pages/followed-companies/followed-companies';
import { Industries } from '../../pages/industries/industries';
import { OldIndustries } from '../../pages/industries/oldIndustries';
import { IndustryByCompanies } from '../../pages/industry-by-companies/industry-by-companies';
import { OldIndustryByCompanies } from '../../pages/industry-by-companies/old-industry-by-companies';
import { LoggedInLandingPage } from '../../pages/logged-in-landing-page/logged-in-landing-page';
import { LoggedOutLandingPage } from '../../pages/logged-out-landing-page/logged-out-landing-page';
import { ManagedCompanies } from '../../pages/managed-companies/managed-companies';
import { MostVisitedCompanies } from '../../pages/most-visited-companies/most-visited-companies';
import { RecentlyClaimedCompanies } from '../../pages/recently-claimed-companies/recently-claimed-companies';
import { RecommendedCompanies } from '../../pages/recommended-companies/recommended-companies';
import { ROUTES } from '../../routes';
import type { CompaniesRoute } from '../../types';
import { getStaticIndustryKey } from '../../utils/static-industry-key';

const RedirectToCompanies: FC = () => (
  <Redirect to={ROUTES.companies} noThrow />
);

export const useCompaniesRoutes = () => {
  const { formatMessage } = useIntl();
  const { isLoggedIn } = useLoginState();

  const showNewLoggedInOverview = useFeatureSwitch(
    'enable_new_logged_in_companies_overview'
  );

  const getIndustryRouteName = (industry: string): string =>
    formatMessage({ id: getStaticIndustryKey(industry) });

  // TODO: Remove old routes once the feature switch is removed `enable_new_logged_in_companies_overview`
  // PLEASE remove them too from the valid routes at the server side
  // core/app-server/src/plugins/redirects/handlers/companies/index.ts
  // this valid routes will make this routes to be redirected to /pages
  const oldRoutes: CompaniesRoute[] = [
    {
      onlyAuthenticated: true,
      path: ROUTES.followedCompanies,
      Component: showNewLoggedInOverview
        ? RedirectToCompanies
        : FollowedCompanies,
    },
    {
      onlyAuthenticated: true,
      path: ROUTES.managedCompanies,
      Component: showNewLoggedInOverview
        ? RedirectToCompanies
        : ManagedCompanies,
    },
    {
      onlyAuthenticated: false,
      path: ROUTES.mostVisitedCompanies,
      Component: showNewLoggedInOverview
        ? RedirectToCompanies
        : MostVisitedCompanies,
      name: formatMessage({ id: 'CP_100_MOST_CLICKED_COMPANIES' }),
    },
    {
      onlyAuthenticated: true,
      path: ROUTES.recentlyClaimedCompanies,
      Component: showNewLoggedInOverview
        ? RedirectToCompanies
        : RecentlyClaimedCompanies,
      name: formatMessage({ id: 'CP_NEW_EMPLOYERS' }),
    },
  ];

  const routes: CompaniesRoute[] = [
    {
      onlyAuthenticated: false,
      path: ROUTES.companies,
      Component: isLoggedIn
        ? showNewLoggedInOverview
          ? LoggedInLandingPage
          : RecommendedCompanies
        : LoggedOutLandingPage,
      name: isLoggedIn
        ? showNewLoggedInOverview
          ? 'something New'
          : 'Recommended Companies'
        : formatMessage({ id: 'SE_LABEL_COMPANIES' }),
    },
    {
      onlyAuthenticated: false,
      path: ROUTES.industries,
      Component: isLoggedIn
        ? showNewLoggedInOverview
          ? RedirectToCompanies
          : OldIndustries
        : Industries,
      name: formatMessage({ id: 'CP_200_INDUSTRIES' }),
    },
    {
      onlyAuthenticated: false,
      path: ROUTES.industry,
      Component: isLoggedIn
        ? showNewLoggedInOverview
          ? IndustryByCompanies
          : OldIndustryByCompanies
        : IndustryByCompanies,
      name: ({ industry }) => getIndustryRouteName(industry),
    },
    {
      onlyAuthenticated: false,
      path: ROUTES.segment,
      Component: isLoggedIn
        ? showNewLoggedInOverview
          ? IndustryByCompanies
          : OldIndustryByCompanies
        : IndustryByCompanies,
      name: ({ industry }) => getIndustryRouteName(industry),
    },
    ...oldRoutes,
  ];

  return { routes };
};
